import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  addNoticeAttachment,
  createNotice,
  getNoticeAttachmentUrl,
  getNoticeOptions,
  getNoticeProperties,
  listNotices,
  previewNoticeAudience,
  removeNoticeAttachment,
  sendNotice,
  sendNoticeTest,
  undoNoticeSend,
  updateNotice,
  uploadNoticeFile,
} from "../noticesApi";
import { canSendCompanyWide } from "../access";
import { AudiencePicker } from "./AudiencePicker";
import { NoticeHistory } from "./NoticeHistory";
import NoticePreview from "./NoticePreview";
import SendConfirm, { UndoBar, needsConfirm } from "./SendConfirm";
import TemplatePicker from "./TemplatePicker";
import { isPhotoFile, isVideoFile, makeVideoPoster, makeWebCopy } from "./mediaDerivatives";
import {
  MAX_NOTICE_FILES,
  MAX_NOTICE_VIDEOS,
  MAX_NOTICE_FILE_BYTES,
  MAX_NOTICE_VIDEO_BYTES,
  VIDEO_EXTENSIONS,
  NOTICE_TYPES,
  TYPE_ICON,
  emptyNotice,
  formatBytes,
  formatWhen,
  isAcceptedFile,
  toLocalInputValue,
} from "./noticeHelpers";
import "./notices.scss";

const AUTOSAVE_MS = 800;

const sameAudience = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The web-sized photo copy and the video poster are bookkeeping made by the browser, not files
 * the manager chose. They never show in the list and never count against the file limit — the
 * server counts them the same way.
 */
const isDerivativeAttachment = (a) => a?.kind === "inline" || a?.kind === "poster";

/**
 * Send Notices. One modal, two tabs: Compose (the notice, who gets it, when and how it goes
 * out) and History (everything sent, scheduled or drafted for this building). Drafts save
 * themselves as you type; files go to storage first and are then recorded on the draft.
 */
const SendNoticeModal = ({ isOpen, onClose, filterData, userData, initialTab = "compose", onSent }) => {
  const canCompany = canSendCompanyWide(userData);
  const [tab, setTab] = useState(initialTab);

  // ---- the draft --------------------------------------------------------
  const [draft, setDraft] = useState(emptyNotice);
  const [draftId, setDraftId] = useState(null);
  const [scope, setScope] = useState("property");
  const [propertyIds, setPropertyIds] = useState([]);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const draftIdRef = useRef(null);
  const dirtyRef = useRef(false);
  const creatingRef = useRef(null);

  // ---- what the picker needs ---------------------------------------------
  const [options, setOptions] = useState(null);
  const [properties, setProperties] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [drafts, setDrafts] = useState([]);

  // ---- delivery ----------------------------------------------------------
  const [when, setWhen] = useState("now"); // now | later
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false); // false | "send" | "test" | "upload"
  const [uploadPct, setUploadPct] = useState(0);
  const [historyKey, setHistoryKey] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const sentIdRef = useRef(null);
  const sentDataRef = useRef(null);
  const fileInputRef = useRef(null);

  const buildingLabel = [filterData.property_name, filterData.building_name].filter(Boolean).join(" · ");

  const targetPayload = useCallback(
    () =>
      scope === "company"
        ? { scope: "company", property_ids: propertyIds }
        : { scope: "property", property_id: filterData.property_id, property_name: filterData.property_name, building_name: filterData.building_name || "" },
    [scope, propertyIds, filterData.property_id, filterData.property_name, filterData.building_name]
  );

  const resetCompose = useCallback(() => {
    setDraft(emptyNotice());
    setDraftId(null);
    draftIdRef.current = null;
    dirtyRef.current = false;
    setSaveState("idle");
    setSavedAt(null);
    setScope("property");
    setPropertyIds([]);
    setWhen("now");
    setScheduledAt("");
    setPreview(null);
    setShowPreview(false);
    setConfirming(false);
  }, []);

  // Open: fresh compose state, load the picker data and any drafts waiting here.
  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab);
    resetCompose();
    if (filterData.property_id) {
      getNoticeOptions(filterData.property_id, filterData.building_name)
        .then((r) => setOptions(r?.data || null))
        .catch(() => setOptions(null));
      listNotices({ property_id: filterData.property_id, building_name: filterData.building_name || "", status: "draft" })
        .then((r) => setDrafts(r?.data || []))
        .catch(() => setDrafts([]));
    }
    if (canCompany) {
      getNoticeProperties()
        .then((r) => setProperties(r?.data?.properties || []))
        .catch(() => setProperties([]));
    }
  }, [isOpen, initialTab, filterData.property_id, filterData.building_name, canCompany, resetCompose]);

  // Live audience count, debounced, whenever the audience or scope changes.
  useEffect(() => {
    if (!isOpen || tab !== "compose") return undefined;
    if (scope === "property" && !filterData.property_id) return undefined;
    setPreviewLoading(true);
    const t = setTimeout(() => {
      previewNoticeAudience({ ...targetPayload(), audience: draft.audience })
        .then((r) => setPreview(r?.data || null))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 500);
    return () => clearTimeout(t);
  }, [isOpen, tab, scope, draft.audience, targetPayload, filterData.property_id]);

  // ---- autosave -----------------------------------------------------------
  const meaningful = draft.title.trim() || draft.message.trim() || draft.attachments.length > 0;

  const persist = useCallback(async () => {
    const body = {
      ...targetPayload(),
      type: draft.type,
      title: draft.title,
      message: draft.message,
      audience: draft.audience,
      options: { sms: !!draft.options.sms, require_acknowledgement: !!draft.options.require_acknowledgement },
    };
    setSaveState("saving");
    try {
      let res;
      if (draftIdRef.current) {
        res = await updateNotice(draftIdRef.current, body);
      } else {
        if (!creatingRef.current) creatingRef.current = createNotice(body);
        res = await creatingRef.current;
        creatingRef.current = null;
        draftIdRef.current = res?.data?._id || null;
        setDraftId(draftIdRef.current);
      }
      setSaveState("saved");
      setSavedAt(new Date());
      return res?.data;
    } catch (e) {
      setSaveState("error");
      creatingRef.current = null;
      throw e;
    }
  }, [draft, targetPayload]);

  useEffect(() => {
    if (!isOpen || !dirtyRef.current || !meaningful) return undefined;
    const t = setTimeout(() => {
      dirtyRef.current = false;
      persist().catch(() => {});
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [isOpen, draft.type, draft.title, draft.message, draft.audience, draft.options.sms, draft.options.require_acknowledgement, scope, propertyIds, meaningful, persist]);

  const edit = (patch) => {
    dirtyRef.current = true;
    setDraft((d) => ({ ...d, ...patch }));
  };
  const editAudience = (audience) => {
    if (sameAudience(audience, draft.audience)) return;
    edit({ audience });
  };

  /** A draft must exist before a file can hang off it. */
  const ensureDraft = async () => {
    if (draftIdRef.current) return draftIdRef.current;
    await persist();
    return draftIdRef.current;
  };

  // ---- files --------------------------------------------------------------
  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (draft.attachments.filter((a) => !isDerivativeAttachment(a)).length + files.length > MAX_NOTICE_FILES) {
      toast.error(`A notice can carry at most ${MAX_NOTICE_FILES} files`);
      return;
    }
    const bad = files.find((f) => !isAcceptedFile(f));
    if (bad) {
      toast.error(`"${bad.name}" is not a PDF, a photo (JPG, PNG, WebP) or a video (MP4, MOV)`);
      return;
    }
    // Video is capped far higher than everything else, so the cap is per file, not one rule.
    const capFor = (f) => (VIDEO_EXTENSIONS.some((e) => String(f.name || "").toLowerCase().endsWith(e)) ? MAX_NOTICE_VIDEO_BYTES : MAX_NOTICE_FILE_BYTES);
    const isVideo = (f) => VIDEO_EXTENSIONS.some((e) => String(f.name || "").toLowerCase().endsWith(e));
    // The server refuses a third video. Catching it here means the manager is told before
    // 75 MB has already been pushed to storage only to be rejected on arrival.
    const videosNow = draft.attachments.filter((a) => !isDerivativeAttachment(a) && String(a.mime || "").startsWith("video/")).length;
    if (videosNow + files.filter(isVideo).length > MAX_NOTICE_VIDEOS) {
      toast.error(`A notice can carry at most ${MAX_NOTICE_VIDEOS} videos`);
      return;
    }

    const big = files.find((f) => f.size > capFor(f));
    if (big) {
      toast.error(`"${big.name}" is larger than ${capFor(big) === MAX_NOTICE_VIDEO_BYTES ? "75 MB" : "10 MB"}`);
      return;
    }
    setBusy("upload");
    try {
      const id = await ensureDraft();
      let attachments = draft.attachments;
      for (const file of files) {
        setUploadPct(0);
        const stored = await uploadNoticeFile(file, (ev) => ev.total && setUploadPct(Math.round((ev.loaded / ev.total) * 100)));
        const res = await addNoticeAttachment(id, { key: stored.key, filename: file.name, size: file.size, mime: file.type });
        attachments = res?.data || attachments;
        // The controller answers with the whole attachments array; the one just pushed is last.
        const parentId = (Array.isArray(res?.data) ? res.data : []).slice(-1)[0]?.attachment_id;

        // A photo gets a web-sized twin for the email; a video gets a still with the play badge
        // drawn in. Both are best-effort: if either fails, the notice still sends without it.
        const derivative = isPhotoFile(file) ? await makeWebCopy(file) : isVideoFile(file) ? await makeVideoPoster(file) : null;
        if (derivative && parentId) {
          try {
            setUploadPct(0);
            const up = await uploadNoticeFile(derivative, (ev) => ev.total && setUploadPct(Math.round((ev.loaded / ev.total) * 100)));
            const dres = await addNoticeAttachment(id, {
              key: up.key,
              filename: derivative.name,
              size: derivative.size,
              mime: "image/jpeg",
              kind: isVideoFile(file) ? "poster" : "inline",
              poster_for: parentId,
            });
            attachments = dres?.data || attachments;
          } catch (err) {
            console.warn("[notices] derivative upload skipped:", err && err.message);
          }
        }
      }
      setDraft((d) => ({ ...d, attachments }));
      setSaveState("saved");
      setSavedAt(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Upload failed");
    } finally {
      setBusy(false);
      setUploadPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = async (attachmentId) => {
    try {
      const res = await removeNoticeAttachment(draftIdRef.current, attachmentId);
      setDraft((d) => ({ ...d, attachments: res?.data || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not remove the file");
    }
  };

  const previewFile = async (attachmentId) => {
    try {
      const res = await getNoticeAttachmentUrl(draftIdRef.current, attachmentId);
      if (res?.data?.url) window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error("Could not open the file");
    }
  };

  // ---- drafts strip ---------------------------------------------------------
  const continueDraft = (n) => {
    dirtyRef.current = false;
    draftIdRef.current = n._id;
    setDraftId(n._id);
    setDraft({
      type: n.type || "General",
      title: n.title || "",
      message: n.message || "",
      attachments: n.attachments || [],
      audience: { ...emptyNotice().audience, ...(n.audience || {}) },
      options: { sms: !!n.options?.sms, require_acknowledgement: !!n.options?.require_acknowledgement, scheduled_at: null },
    });
    setScope(n.scope === "company" ? "company" : "property");
    setPropertyIds(n.property_ids || []);
    setSaveState("saved");
    setSavedAt(n.updatedAt ? new Date(n.updatedAt) : null);
    setTab("compose");
  };

  // ---- send / test ------------------------------------------------------------
  /**
   * Everything that used to happen the moment a send returned: tell the page above, refresh
   * history, empty the compose tab and show what just went out. With the undo window it runs
   * thirty seconds later instead — or straight away for a scheduled send, which has no window.
   */
  const finishSend = useCallback(
    (data) => {
      if (onSent) onSent(data);
      setHistoryKey((k) => k + 1);
      resetCompose();
      setTab("history");
    },
    [onSent, resetCompose]
  );

  const validate = () => {
    if (!draft.title.trim()) return "Give the notice a title.";
    if (!draft.message.trim() && draft.attachments.length === 0) return "Add the notice file or write a message.";
    if (scope === "property" && !filterData.property_id) return "Pick a property and building first.";
    if (when === "later") {
      const d = scheduledAt ? new Date(scheduledAt) : null;
      if (!d || isNaN(d.getTime())) return "Pick a date and time to send.";
      if (d.getTime() < Date.now() + 60_000) return "The send time has to be in the future.";
    }
    return "";
  };

  const handleTest = async () => {
    const problem = !draft.title.trim() ? "Give the notice a title first." : "";
    if (problem) return toast.error(problem);
    setBusy("test");
    try {
      const id = await ensureDraft();
      const res = await sendNoticeTest(id);
      toast.success(res?.message || "Test sent");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "The test could not be sent");
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    const problem = validate();
    if (problem) return toast.error(problem);
    const total = preview?.summary?.total || 0;
    const noEmail = preview?.summary?.without_email || 0;
    const whenText = when === "later" ? ` on ${formatWhen(scheduledAt)}` : " now";
    const ok = await Swal.fire({
      title: when === "later" ? "Schedule this notice?" : "Send this notice?",
      html: `<div style="text-align:left;font-size:14px;line-height:1.5">
        <div><strong>${total}</strong> resident${total === 1 ? "" : "s"} will get <strong>${draft.title.replace(/</g, "&lt;")}</strong>${whenText}.</div>
        ${noEmail ? `<div style="color:#b45309;margin-top:6px">${noEmail} of them have no email address and will be listed as missed.</div>` : ""}
        ${draft.options.sms ? `<div style="margin-top:6px">A text message with the link also goes to everyone with a mobile number.</div>` : ""}
      </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: when === "later" ? "Schedule" : "Send",
      confirmButtonColor: "#0c447c",
    });
    if (!ok.isConfirmed) return;
    setBusy("send");
    try {
      const id = await ensureDraft();
      const res = await sendNotice(id, when === "later" ? new Date(scheduledAt).toISOString() : null);
      toast.success(res?.message || "Sent");
      if (when === "later") {
        // A scheduled notice can be cancelled from History for as long as it is waiting; there
        // is nothing to undo in the next thirty seconds.
        finishSend(res?.data);
        return;
      }
      // The server holds a "send now" for thirty seconds. The modal stays open for exactly
      // that long so the bar can stop it, then does what it always did.
      sentIdRef.current = id;
      sentDataRef.current = res?.data;
      setUndoing(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "The notice could not be sent");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    // Closing during the countdown lets the send stand — the server still dispatches it — so the
    // page above is told about it exactly as if the window had run out.
    if (undoing) {
      setUndoing(false);
      finishSend(sentDataRef.current);
    }
    onClose();
  };

  const originalFiles = draft.attachments.filter((a) => !isDerivativeAttachment(a));
  const noticeFile = originalFiles[0];
  const extras = originalFiles.slice(1);
  const saveLabel = useMemo(() => {
    if (saveState === "saving") return "Saving…";
    if (saveState === "error") return "Could not save the draft";
    if (saveState === "saved" && savedAt) return `Draft saved ${savedAt.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}`;
    return meaningful ? "Unsaved changes" : "";
  }, [saveState, savedAt, meaningful]);

  return (
    <Modal isOpen={isOpen} toggle={close} size="xl" modalClassName="rn-modal" contentClassName="rn-content" centered scrollable>
      <div className="rn-head">
        <span className="rn-head-ic">
          <FeatherIcon icon="send" size={17} />
        </span>
        <div className="rn-head-text">
          <div className="rn-head-title">Notices</div>
          <div className="rn-head-sub">{buildingLabel || "Pick a property to send notices"}</div>
        </div>
        <div className="rn-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "compose"} className={tab === "compose" ? "on" : ""} onClick={() => setTab("compose")}>
            New notice
          </button>
          <button type="button" role="tab" aria-selected={tab === "history"} className={tab === "history" ? "on" : ""} onClick={() => setTab("history")}>
            History
          </button>
        </div>
        <button type="button" className="rn-close" onClick={close} aria-label="Close">
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>

      {tab === "history" ? (
        <div className="rn-body">
          <NoticeHistory
            filterData={filterData}
            scopeFilter={canCompany && scope === "company" ? "company" : "property"}
            refreshKey={historyKey}
            onContinueDraft={continueDraft}
            onDuplicated={continueDraft}
          />
        </div>
      ) : (
        <>
          <div className="rn-body">
            {drafts.filter((d) => d._id !== draftId).length > 0 && (
              <div className="rn-drafts">
                <FeatherIcon icon="file-text" size={13} />
                <span>Drafts:</span>
                {drafts
                  .filter((d) => d._id !== draftId)
                  .map((d) => (
                    <button key={d._id} type="button" className="rn-link" onClick={() => continueDraft(d)}>
                      {d.title || "Untitled"} · {formatWhen(d.updatedAt)}
                    </button>
                  ))}
              </div>
            )}

            {/* ---- 1. The notice ---- */}
            <section className="rn-section">
              <h3>
                <span className="n">1</span> The notice
              </h3>
              <TemplatePicker
                draft={draft}
                canDelete={canCompany}
                onApply={(t) =>
                  edit({
                    type: t.type || draft.type,
                    title: t.title || draft.title,
                    message: t.message || draft.message,
                    options: { ...draft.options, ...(t.options || {}) },
                  })
                }
              />
              <div className="rn-types" role="group" aria-label="Type">
                {NOTICE_TYPES.map((t) => (
                  <button key={t} type="button" className={`rn-chip${draft.type === t ? " on" : ""}`} aria-pressed={draft.type === t} onClick={() => edit({ type: t })} disabled={!!busy}>
                    <FeatherIcon icon={TYPE_ICON[t]} size={13} /> {t}
                  </button>
                ))}
              </div>
              <label className="rn-field">
                <span>Title</span>
                <input type="text" value={draft.title} maxLength={160} placeholder="e.g. Water shut-off on Tuesday, 9 to 11 am" onChange={(e) => edit({ title: e.target.value })} disabled={!!busy} />
              </label>
              <label className="rn-field">
                <span>Message</span>
                <textarea rows={4} value={draft.message} maxLength={4000} placeholder="What residents need to know. This goes in the email above the attached notice." onChange={(e) => edit({ message: e.target.value })} disabled={!!busy} />
              </label>

              <div
                className={`rn-files${draft.attachments.length ? "" : " is-empty"}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add("over");
                }}
                onDragLeave={(e) => e.currentTarget.classList.remove("over")}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("over");
                  addFiles(e.dataTransfer.files);
                }}
              >
                {noticeFile ? (
                  <div className="rn-file is-notice">
                    <FeatherIcon icon="file-text" size={15} />
                    <span className="n">
                      {noticeFile.filename} <em>attached to the email</em>
                    </span>
                    <span className="s">{formatBytes(noticeFile.size)}</span>
                    <button type="button" className="rn-ib" title="Open" onClick={() => previewFile(noticeFile.attachment_id)} disabled={!!busy}>
                      <FeatherIcon icon="eye" size={13} />
                    </button>
                    <button type="button" className="rn-ib danger" title="Remove" onClick={() => removeFile(noticeFile.attachment_id)} disabled={!!busy}>
                      <FeatherIcon icon="trash-2" size={13} />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="rn-dropzone" onClick={() => fileInputRef.current?.click()} disabled={!!busy}>
                    <span className="ic">
                      <FeatherIcon icon="upload-cloud" size={18} />
                    </span>
                    <span className="t">Drop the notice here, or browse</span>
                    <span className="b">PDF, photos (JPG, PNG, WebP) up to 10 MB, video (MP4, MOV) up to 75 MB</span>
                  </button>
                )}
                {extras.map((a) => (
                  <div key={a.attachment_id} className="rn-file">
                    <FeatherIcon icon="paperclip" size={14} />
                    <span className="n">{a.filename}</span>
                    <span className="s">{formatBytes(a.size)}</span>
                    <button type="button" className="rn-ib" title="Open" onClick={() => previewFile(a.attachment_id)} disabled={!!busy}>
                      <FeatherIcon icon="eye" size={13} />
                    </button>
                    <button type="button" className="rn-ib danger" title="Remove" onClick={() => removeFile(a.attachment_id)} disabled={!!busy}>
                      <FeatherIcon icon="trash-2" size={13} />
                    </button>
                  </div>
                ))}
                {noticeFile && originalFiles.length < MAX_NOTICE_FILES && (
                  <button type="button" className="rn-link rn-addfile" onClick={() => fileInputRef.current?.click()} disabled={!!busy}>
                    <FeatherIcon icon="plus" size={12} /> Add another file
                  </button>
                )}
                {busy === "upload" && (
                  <div className="rn-progress" aria-live="polite">
                    <Spinner size="sm" /> Uploading {uploadPct ? `${uploadPct}%` : ""}
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.mp4,.m4v,.mov" multiple hidden onChange={(e) => addFiles(e.target.files)} />
              </div>
            </section>

            {/* ---- 2. Who gets it ---- */}
            <section className="rn-section">
              <h3>
                <span className="n">2</span> Who gets it
              </h3>
              <AudiencePicker
                audience={draft.audience}
                onChange={editAudience}
                options={options}
                preview={preview}
                loading={previewLoading}
                scope={scope}
                canCompanyWide={canCompany}
                onScopeChange={(s) => {
                  dirtyRef.current = true;
                  setScope(s);
                }}
                properties={properties}
                selectedPropertyIds={propertyIds}
                onPropertyIdsChange={(ids) => {
                  dirtyRef.current = true;
                  setPropertyIds(ids);
                }}
                buildingLabel={filterData.building_name ? `This building` : `This property`}
                disabled={!!busy}
              />
            </section>

            {/* The panel needs a saved draft to preview against; autosave has made one by the
                time a title exists, which is exactly when the Preview button becomes enabled. */}
            {showPreview && draftId ? (
              <section className="rn-section">
                <NoticePreview noticeId={draftId} draft={draft} onClose={() => setShowPreview(false)} />
              </section>
            ) : null}

            {/* ---- 3. Delivery ---- */}
            <section className="rn-section">
              <h3>
                <span className="n">3</span> Delivery
              </h3>
              <div className="rn-delivery">
                <div className="rn-when" role="group" aria-label="When">
                  <button type="button" className={`rn-seg${when === "now" ? " on" : ""}`} onClick={() => setWhen("now")} disabled={!!busy}>
                    <FeatherIcon icon="zap" size={13} /> Send now
                  </button>
                  <button type="button" className={`rn-seg${when === "later" ? " on" : ""}`} onClick={() => setWhen("later")} disabled={!!busy}>
                    <FeatherIcon icon="clock" size={13} /> Schedule
                  </button>
                  {when === "later" && (
                    <input type="datetime-local" className="rn-datetime" value={scheduledAt} min={toLocalInputValue(new Date())} onChange={(e) => setScheduledAt(e.target.value)} aria-label="Send at" disabled={!!busy} />
                  )}
                </div>
                <label className="rn-check">
                  <input type="checkbox" checked={!!draft.options.sms} onChange={(e) => edit({ options: { ...draft.options, sms: e.target.checked } })} disabled={!!busy} />
                  <span>
                    Also text residents who have a mobile number
                    <small>A short message with the link{preview?.summary ? ` · ${preview.summary.with_phone} of ${preview.summary.total} have a number` : ""}</small>
                  </span>
                </label>
                <label className="rn-check">
                  <input
                    type="checkbox"
                    checked={!!draft.options.require_acknowledgement}
                    onChange={(e) => edit({ options: { ...draft.options, require_acknowledgement: e.target.checked } })}
                    disabled={!!busy}
                  />
                  <span>
                    Require acknowledgement
                    <small>
                      Residents confirm they&apos;ve read it &mdash; a &quot;Got it&quot; button if there&apos;s a file to view, or automatically
                      when they open a message-only notice. History records who did. Use it for entry, inspection and rent notices.
                    </small>
                  </span>
                </label>
                <p className="rn-muted rn-delivery-note">
                  Each resident gets their own email from <strong>{userData?.company || "the company"}</strong> with the notice attached and a personal link that works for 60 days. Replies come to {userData?.email || "you"}.
                </p>
              </div>
            </section>
          </div>

          {confirming ? (
            <SendConfirm
              total={preview?.summary?.total || 0}
              units={preview?.summary?.units || 0}
              where={scope === "company" ? "Every property in the company" : [filterData.property_name, filterData.building_name].filter(Boolean).join(", ")}
              channels={draft.options.sms ? "email + text" : "email"}
              companyWide={scope === "company"}
              scheduled={when === "later"}
              propertyCount={propertyIds?.length}
              busy={!!busy}
              onCancel={() => setConfirming(false)}
              onConfirm={() => {
                setConfirming(false);
                handleSend();
              }}
            />
          ) : null}

          {undoing ? (
            <UndoBar
              seconds={30}
              onDone={() => {
                setUndoing(false);
                finishSend(sentDataRef.current);
              }}
              onUndo={async () => {
                try {
                  await undoNoticeSend(sentIdRef.current);
                  toast.success("Stopped. Nothing was sent.");
                  setUndoing(false);
                  setHistoryKey((k) => k + 1);
                } catch (e) {
                  toast.error(e?.response?.data?.message || "It had already gone out");
                  setUndoing(false);
                  finishSend(sentDataRef.current);
                }
              }}
            />
          ) : null}

          <div className="rn-foot">
            <span className={`rn-save ${saveState}`}>
              {saveState === "saving" ? <Spinner size="sm" /> : saveState === "saved" ? <FeatherIcon icon="check" size={13} /> : null} {saveLabel}
            </span>
            <div className="rn-foot-actions">
              <button type="button" className="rn-btn" onClick={handleTest} disabled={!!busy || !draft.title.trim()}>
                {busy === "test" ? <Spinner size="sm" /> : <FeatherIcon icon="mail" size={13} />} Send me a test
              </button>
              <button type="button" className="rn-btn" onClick={close} disabled={!!busy}>
                Close
              </button>
              <button
                type="button"
                className="rn-btn"
                onClick={() => setShowPreview((v) => !v)}
                disabled={!!busy || !draft.title.trim()}
                title={draft.title.trim() ? "See it as a resident will" : "Give the notice a title first"}
              >
                <FeatherIcon icon="eye" size={15} /> {showPreview ? "Hide preview" : "Preview"}
              </button>
              <button
                type="button"
                className="rn-btn rn-btn-primary"
                onClick={() => (needsConfirm(preview?.summary?.total) ? setConfirming(true) : handleSend())}
                disabled={!!busy || undoing || confirming || !meaningful || (preview?.summary?.total || 0) === 0}
              >
                {busy === "send" ? <Spinner size="sm" /> : <FeatherIcon icon={when === "later" ? "clock" : "send"} size={13} />}
                {when === "later" ? "Schedule" : "Send"}
                {preview?.summary?.total ? ` to ${preview.summary.total}` : ""}
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export { SendNoticeModal };
